import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Check, Target, Flag, X, Minus, Plus, Trophy } from 'lucide-react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { RestTimer } from '@/components/RestTimer';
import { cn } from '@/lib/utils';
import {
  useSquatSession,
  useUpdateSquatSet,
  useCompleteSquatSession,
  toNumber,
} from '@/hooks/useSquatProgram';
import type { SquatSet } from '@/types';

// Oletuslepoaika sarjan perusteella
function defaultRestSeconds(set: SquatSet): number {
  if (set.isWorkSet) return 300; // 5 min työsarja
  if (set.targetReps <= 2) return 210; // 3,5 min kakkossarjat
  return 120; // 2 min lämmittelyt
}

export function SquatSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { data: session, isLoading } = useSquatSession(sessionId);
  const updateSet = useUpdateSquatSet(sessionId);
  const completeSession = useCompleteSquatSession();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [restTargetEnd, setRestTargetEnd] = useState<number | null>(null);
  const [restDuration, setRestDuration] = useState(120);
  const [showRepAdjust, setShowRepAdjust] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPRCelebration, setShowPRCelebration] = useState<number | null>(null);

  // Siirry ensimmäiseen suorittamattomaan sarjaan kun data latautuu
  useEffect(() => {
    if (!session) return;
    const firstIncomplete = session.sets.findIndex((s) => !s.completed);
    if (firstIncomplete >= 0) {
      setCurrentIndex(firstIncomplete);
    }
  }, [session?.id]);

  const sets = session?.sets ?? [];
  const currentSet = sets[currentIndex];
  const completedCount = sets.filter((s) => s.completed).length;
  const allDone = sets.length > 0 && completedCount === sets.length;

  // Voit selata aiempia ja seuraavia sarjoja
  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(sets.length - 1, i + 1));

  const handleComplete = async (actualReps: number) => {
    if (!currentSet) return;
    setShowRepAdjust(false);

    await updateSet.mutateAsync({
      setId: currentSet.id,
      data: { actualReps, completed: actualReps > 0 },
    });

    // Käynnistä lepoajastin, paitsi jos viimeinen sarja
    if (currentIndex < sets.length - 1) {
      const d = defaultRestSeconds(currentSet);
      setRestDuration(d);
      setRestTargetEnd(Date.now() + d * 1000);
    }
  };

  const handleSkip = async () => {
    if (!currentSet) return;
    await updateSet.mutateAsync({
      setId: currentSet.id,
      data: { actualReps: 0, completed: true },
    });
    if (currentIndex < sets.length - 1) {
      goNext();
    }
  };

  const handleRestComplete = () => {
    setRestTargetEnd(null);
    // Siirry seuraavaan sarjaan automaattisesti
    if (currentIndex < sets.length - 1) {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handleFinishWorkout = async (rpe: number | null, notes: string) => {
    if (!sessionId) return;
    const result = await completeSession.mutateAsync({
      sessionId,
      data: {
        rpe: rpe ?? undefined,
        notes: notes.trim() || undefined,
      },
    });
    setShowCompleteModal(false);
    if (result.programCompleted && result.newPR) {
      setShowPRCelebration(result.newPR);
    } else {
      navigate('/kyykkyohjelma');
    }
  };

  if (isLoading || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentSet) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Sessiossa ei ole sarjoja</p>
      </div>
    );
  }

  const weight = toNumber(currentSet.weight);
  const percentage = Math.round(toNumber(currentSet.percentage));

  return (
    <div className="max-w-xl mx-auto">
      {/* Yläpalkki */}
      <header className="flex items-center justify-between mb-4 gap-3">
        <button
          type="button"
          onClick={() => navigate('/kyykkyohjelma')}
          aria-label="Takaisin ohjelmaan"
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0 text-center">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Viikko {session.weekNumber}
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            Sarja {currentIndex + 1} / {sets.length}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCompleteModal(true)}
          className={cn(
            'min-h-11 px-3 rounded-xl text-sm font-semibold touch-manipulation',
            allDone
              ? 'bg-green-600 text-white active:bg-green-700'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600'
          )}
        >
          <Flag className="h-4 w-4 inline-block mr-1" />
          Päätä
        </button>
      </header>

      {/* Edistymispalkki */}
      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-primary-600 dark:bg-primary-500 transition-all duration-500"
          style={{ width: `${(completedCount / sets.length) * 100}%` }}
        />
      </div>

      {/* Lepoajastin näkyvissä kun aktiivinen */}
      {restTargetEnd != null && (
        <div className="mb-6">
          <RestTimer
            targetEnd={restTargetEnd}
            durationSeconds={restDuration}
            onComplete={handleRestComplete}
            onSkip={handleRestComplete}
            onAdjust={(delta) =>
              setRestTargetEnd((prev) =>
                prev == null ? prev : Math.max(Date.now(), prev + delta * 1000)
              )
            }
          />
        </div>
      )}

      {/* Sarjan esittely */}
      <section className="flex flex-col items-center justify-center py-8 px-4">
        {currentSet.isWorkSet && (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-base font-semibold mb-4">
            <Target className="h-5 w-5" />
            Työsarja
          </div>
        )}

        <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
          {percentage} % 1RM
        </div>

        <div className="text-7xl font-bold text-gray-900 dark:text-white tabular-nums leading-none mb-2">
          {weight}
          <span className="text-3xl text-gray-500 dark:text-gray-400 ml-2 font-medium">
            kg
          </span>
        </div>

        <div className="text-3xl text-gray-700 dark:text-gray-300 font-medium mb-1">
          × {currentSet.targetReps} toistoa
        </div>

        {currentSet.completed && (
          <div className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
            ✓ Tehty: {currentSet.actualReps} toistoa
          </div>
        )}
      </section>

      {/* Toimintopainikkeet */}
      <div className="space-y-3 mb-6">
        {!currentSet.completed && (
          <>
            <button
              type="button"
              onClick={() => handleComplete(currentSet.targetReps)}
              disabled={updateSet.isPending}
              className={cn(
                'w-full min-h-16 rounded-2xl text-lg font-bold',
                'bg-green-600 text-white active:bg-green-700',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'active:scale-[0.98] transition-all touch-manipulation',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-green-400'
              )}
            >
              <Check className="h-6 w-6 inline-block mr-2" />
              Tehty ✓
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowRepAdjust(true)}
                className="flex-1 min-h-12 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation"
              >
                Säädä toistot
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 min-h-12 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-red-600 dark:text-red-400 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation"
              >
                Hylkää sarja
              </button>
            </div>
          </>
        )}

        {currentSet.completed && currentIndex < sets.length - 1 && (
          <button
            type="button"
            onClick={goNext}
            className={cn(
              'w-full min-h-14 rounded-2xl text-base font-semibold',
              'bg-primary-600 text-white active:bg-primary-700',
              'active:scale-[0.98] transition-all touch-manipulation'
            )}
          >
            Seuraava sarja
            <ChevronRight className="h-5 w-5 inline-block ml-1" />
          </button>
        )}

        {allDone && (
          <button
            type="button"
            onClick={() => setShowCompleteModal(true)}
            className={cn(
              'w-full min-h-16 rounded-2xl text-lg font-bold',
              'bg-green-600 text-white active:bg-green-700',
              'active:scale-[0.98] transition-all touch-manipulation'
            )}
          >
            <Flag className="h-6 w-6 inline-block mr-2" />
            Päätä treeni
          </button>
        )}
      </div>

      {/* Sarjanumerot selattavana */}
      <div className="flex items-center justify-between gap-2 mb-6">
        <button
          type="button"
          onClick={goPrev}
          disabled={currentIndex === 0}
          aria-label="Edellinen sarja"
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 disabled:opacity-40 touch-manipulation"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <SetNumberPicker
          sets={sets}
          currentIndex={currentIndex}
          onSelect={setCurrentIndex}
        />
        <button
          type="button"
          onClick={goNext}
          disabled={currentIndex >= sets.length - 1}
          aria-label="Seuraava sarja"
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 disabled:opacity-40 touch-manipulation"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Toistojen säätö -modaali */}
      {showRepAdjust && currentSet && (
        <RepAdjustModal
          initial={currentSet.targetReps}
          target={currentSet.targetReps}
          onSave={(reps) => handleComplete(reps)}
          onCancel={() => setShowRepAdjust(false)}
        />
      )}

      {/* Päätä treeni -modaali */}
      {showCompleteModal && (
        <CompleteSessionModal
          isLoading={completeSession.isPending}
          onSave={handleFinishWorkout}
          onCancel={() => setShowCompleteModal(false)}
        />
      )}

      {/* PR-juhla! */}
      {showPRCelebration !== null && (
        <PRCelebrationModal
          newPR={showPRCelebration}
          onClose={() => {
            setShowPRCelebration(null);
            navigate('/kyykkyohjelma');
          }}
        />
      )}
    </div>
  );
}

// ============================================
// Sarjanumeroiden horisontaalinen valitsin
// ============================================

interface SetNumberPickerProps {
  sets: SquatSet[];
  currentIndex: number;
  onSelect: (index: number) => void;
}

function SetNumberPicker({ sets, currentIndex, onSelect }: SetNumberPickerProps) {
  return (
    <div className="flex-1 flex gap-1 overflow-x-auto scrollbar-hide px-1">
      {sets.map((s, i) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={`Siirry sarjaan ${s.setNumber}`}
          aria-current={i === currentIndex ? 'step' : undefined}
          className={cn(
            'shrink-0 min-w-[40px] h-10 rounded-lg text-sm font-semibold touch-manipulation',
            'transition-colors',
            i === currentIndex
              ? 'bg-primary-600 text-white'
              : s.completed
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
            s.isWorkSet && i !== currentIndex && !s.completed && 'ring-2 ring-amber-400'
          )}
        >
          {s.setNumber}
        </button>
      ))}
    </div>
  );
}

// ============================================
// Toistojen säätö
// ============================================

interface RepAdjustModalProps {
  initial: number;
  target: number;
  onSave: (reps: number) => void;
  onCancel: () => void;
}

function RepAdjustModal({ initial, target, onSave, onCancel }: RepAdjustModalProps) {
  const [reps, setReps] = useState(initial);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Säädä toistot
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Sulje"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 active:bg-gray-100 dark:active:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Tavoite oli {target} toistoa.
        </p>

        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => setReps((r) => Math.max(0, r - 1))}
            className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation flex items-center justify-center"
          >
            <Minus className="h-6 w-6" />
          </button>
          <div className="text-6xl font-bold tabular-nums text-gray-900 dark:text-white min-w-[100px] text-center">
            {reps}
          </div>
          <button
            type="button"
            onClick={() => setReps((r) => Math.min(50, r + 1))}
            className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation flex items-center justify-center"
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => onSave(reps)}
          className="w-full min-h-[56px] rounded-xl bg-primary-600 text-white font-semibold active:bg-primary-700 touch-manipulation"
        >
          Tallenna
        </button>
      </div>
    </div>
  );
}

// ============================================
// Päätä treeni -modaali (RPE + muistiinpanot)
// ============================================

interface CompleteSessionModalProps {
  isLoading: boolean;
  onSave: (rpe: number | null, notes: string) => void;
  onCancel: () => void;
}

function CompleteSessionModal({ isLoading, onSave, onCancel }: CompleteSessionModalProps) {
  const [rpe, setRpe] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const rpeOptions = useMemo(() => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], []);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Päätä treeni
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Sulje"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 active:bg-gray-100 dark:active:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* RPE */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Kuinka rankka treeni oli? (RPE 1–10)
          </label>
          <div className="grid grid-cols-5 gap-2">
            {rpeOptions.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRpe(rpe === n ? null : n)}
                aria-label={`RPE ${n}`}
                aria-pressed={rpe === n}
                className={cn(
                  'min-h-[48px] rounded-xl text-base font-semibold touch-manipulation',
                  rpe === n
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 active:bg-gray-200 dark:active:bg-gray-600'
                )}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            1 = todella helppo, 10 = maksimiyritys
          </p>
        </div>

        {/* Muistiinpanot */}
        <div className="mb-6">
          <label
            htmlFor="session-notes"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Muistiinpanot (valinnainen)
          </label>
          <textarea
            id="session-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Tekniikka, tuntuma, mitä muuttaisit..."
            className="input w-full"
          />
        </div>

        <button
          type="button"
          onClick={() => onSave(rpe, notes)}
          disabled={isLoading}
          className="w-full min-h-[56px] rounded-xl bg-green-600 text-white font-semibold active:bg-green-700 disabled:opacity-60 touch-manipulation"
        >
          {isLoading ? 'Tallennetaan...' : 'Tallenna ja sulje'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// PR-juhla
// ============================================

function PRCelebrationModal({ newPR, onClose }: { newPR: number; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <Trophy className="h-16 w-16 mx-auto text-amber-500 mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Uusi ennätys!
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Onnittelut, uusi 1RM on tallennettu:
        </p>
        <div className="text-5xl font-bold text-primary-600 dark:text-primary-400 mb-6">
          {newPR} kg
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full min-h-[56px] rounded-xl bg-primary-600 text-white font-semibold active:bg-primary-700 touch-manipulation"
        >
          Hienoa!
        </button>
      </div>
    </div>
  );
}
